import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowLeft, CheckCircle, Users, FileText, Target, TrendingUp, 
  Calendar, Building2, Mail, Phone, Loader2, ArrowRight,
  Shield, Award, BarChart3, Sparkles, ChevronRight, DollarSign,
  BookOpen, Handshake, Globe, LineChart, Clock, Landmark, Briefcase,
  ExternalLink, Star
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const PARTNER_TYPES = [
  { id: "cdfi", label: "CDFIs", icon: Landmark, desc: "Strengthen borrower readiness, reduce default risk, and demonstrate measurable impact to your investors." },
  { id: "sba", label: "SBA Lenders", icon: Building2, desc: "Equip SBA borrowers with the operational clarity they need to succeed — before and after funding." },
  { id: "grant", label: "Grant Programs", icon: DollarSign, desc: "Give grantees the structure, accountability, and reporting that turns funding into sustainable growth." },
  { id: "econ_dev", label: "Economic Development Orgs", icon: Globe, desc: "Scale your business development impact with consultant-led support and AI-powered tools for your communities." }
];

const OUTCOMES = [
  { stat: "87%", label: "of participants report increased business clarity within 30 days" },
  { stat: "3x", label: "faster milestone completion vs. unsupported entrepreneurs" },
  { stat: "13", label: "section impact & accountability reports generated automatically" },
  { stat: "50+", label: "data points tracked per participant for accountability" }
];

const PartnershipInquiry = () => {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    organization_name: "",
    contact_name: "",
    email: "",
    phone: "",
    organization_type: "",
    estimated_participants: "",
    engagement_type: "90_day",
    interest_level: "explore",
    timeline: "",
    notes: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.organization_name || !formData.contact_name || !formData.email) {
      toast.error("Please fill in required fields");
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch(`${API}/api/partnership/inquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        setSubmitted(true);
        toast.success("Inquiry submitted! We'll be in touch soon.");
      } else {
        toast.error("Failed to submit inquiry");
      }
    } catch (error) {
      toast.error("Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToForm = () => {
    setShowForm(true);
    setTimeout(() => document.getElementById("partnership-form")?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-neutral-950 text-white">
        <div className="max-w-3xl mx-auto px-4 py-20">
          <div className="text-center">
            <div className="w-20 h-20 bg-[#39FF14]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-[#39FF14]" />
            </div>
            <h1 className="text-3xl font-bold mb-4">Thank You for Your Interest!</h1>
            <p className="text-neutral-400 mb-8 max-w-md mx-auto">
              Your partnership inquiry has been received. Our team will review your submission and reach out within 1-2 business days.
            </p>
            <div className="bg-neutral-900 rounded-xl p-6 mb-8 text-left border border-neutral-800">
              <h3 className="font-semibold mb-3">What happens next:</h3>
              <ul className="space-y-3 text-sm text-neutral-300">
                {["Our partnership team reviews your inquiry and goals", "We prepare a customized proposal aligned to your program", "A team member schedules a discovery conversation with you"].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#39FF14] mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex gap-4 justify-center">
              <Button variant="outline" onClick={() => navigate("/")} className="border-neutral-700 text-white hover:bg-neutral-800">
                <ArrowLeft className="w-4 h-4 mr-2" /> Return Home
              </Button>
              <Button className="bg-[#39FF14] hover:bg-[#32E010] text-black" onClick={() => window.open("https://calendar.app.google/i7u5uTwU3QBcMopM8", "_blank")}>
                <Calendar className="w-4 h-4 mr-2" /> Schedule Partnership Conversation
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-neutral-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center space-x-3">
              <img src="/mybiz-logo.png" alt="My Biz Consulting" className="h-10 w-auto" />
              <div>
                <p className="text-sm font-semibold text-neutral-900 leading-tight">My Biz Consulting</p>
                <p className="text-[10px] text-[#39FF14] font-medium uppercase tracking-widest">Business Solutions Center</p>
              </div>
            </Link>
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate("/pricing")} className="text-sm hidden sm:inline-flex">View Services</Button>
              <Button className="bg-neutral-900 hover:bg-neutral-800 text-white text-sm" onClick={() => window.open("https://calendar.app.google/i7u5uTwU3QBcMopM8", "_blank")}>
                <Calendar className="w-4 h-4 mr-2" /> Schedule Conversation
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-24 pb-16 bg-gradient-to-b from-neutral-950 via-neutral-900 to-neutral-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage: "radial-gradient(circle at 20% 50%, #39FF14 0%, transparent 50%), radial-gradient(circle at 80% 50%, #39FF14 0%, transparent 50%)"}} />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center relative z-10 pt-8">
          <Badge className="bg-[#39FF14]/20 text-[#39FF14] border-[#39FF14]/30 mb-6">For CDFIs, SBA Lenders, Grant Programs &amp; Economic Development Organizations</Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
            Turn Funded Entrepreneurs Into
            <span className="block text-[#39FF14]">Thriving Businesses</span>
          </h1>
          <p className="text-lg sm:text-xl text-neutral-300 max-w-3xl mx-auto mb-4">
            Partner with My Biz Consulting to give your borrowers, grantees, and program participants 
            the <strong className="text-white">consultant-led strategy and AI-powered tools</strong> they 
            need to succeed — before, during, and after funding.
          </p>
          <p className="text-sm text-neutral-500 max-w-2xl mx-auto mb-10">
            Reduce default risk. Strengthen accountability. Demonstrate measurable impact to your stakeholders.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-[#39FF14] hover:bg-[#32E010] text-black font-semibold h-14 px-8 text-base"
              onClick={() => window.open("https://calendar.app.google/i7u5uTwU3QBcMopM8", "_blank")}
              data-testid="hero-schedule-btn"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Schedule Partnership Conversation
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-neutral-600 text-white hover:bg-white/10 h-14 px-8 text-base"
              onClick={scrollToForm}
            >
              <Mail className="w-5 h-5 mr-2" />
              Submit Inquiry
            </Button>
          </div>
        </div>
      </section>

      {/* The Problem We Solve */}
      <section className="py-16 px-4 sm:px-6 bg-neutral-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 mb-4">The Challenge You Face</h2>
            <p className="text-lg text-neutral-600 max-w-3xl mx-auto">
              Capital alone doesn't create success. Many funded entrepreneurs lack the operational 
              structure, strategic clarity, and accountability needed to grow sustainably.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Shield, title: "High Default Risk", desc: "Without business development support, borrowers struggle to meet milestones and repay on schedule." },
              { icon: BarChart3, title: "No Impact Visibility", desc: "Hard to demonstrate ROI to investors, boards, and regulators without structured outcome data." },
              { icon: Users, title: "Limited Support Capacity", desc: "Your team can't provide hands-on business coaching to every entrepreneur in your portfolio." }
            ].map((item, idx) => (
              <Card key={idx} className="border-neutral-200 bg-white">
                <CardContent className="p-6">
                  <item.icon className="w-8 h-8 text-red-500/70 mb-4" />
                  <h3 className="font-semibold text-neutral-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-neutral-600">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="py-16 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 mb-4">Built for Organizations Like Yours</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {PARTNER_TYPES.map((type) => (
              <Card key={type.id} className="border-2 border-neutral-200 hover:border-[#39FF14]/50 transition-colors group">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-neutral-900 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#39FF14]/10 transition-colors">
                      <type.icon className="w-6 h-6 text-[#39FF14]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-neutral-900 mb-2">{type.label}</h3>
                      <p className="text-sm text-neutral-600">{type.desc}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-4 sm:px-6 bg-neutral-950 text-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">How the Partnership Works</h2>
            <p className="text-neutral-400 max-w-2xl mx-auto">
              We integrate seamlessly with your existing programs to provide structured business development support.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "01", title: "Before Funding", icon: Shield, items: ["Business Clarity Assessment to gauge readiness", "Identify gaps in operations, strategy, and finances", "Reduce risk before capital is deployed"] },
              { step: "02", title: "During Funding", icon: Target, items: ["Consultant-led strategic guidance via R.A.P.I.D. framework", "AI-powered tools for planning, SOPs, and execution", "Milestone tracking and accountability"] },
              { step: "03", title: "After Funding", icon: TrendingUp, items: ["Sustainability planning and growth optimization", "Impact & Accountability Reports (13-section PDF)", "Long-term community and peer support network"] }
            ].map((phase, idx) => (
              <div key={idx} className="relative">
                <div className="text-6xl font-black text-[#39FF14]/10 absolute -top-2 -left-2">{phase.step}</div>
                <div className="relative z-10 pt-8">
                  <phase.icon className="w-8 h-8 text-[#39FF14] mb-4" />
                  <h3 className="text-xl font-semibold mb-4">{phase.title}</h3>
                  <ul className="space-y-3">
                    {phase.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-neutral-300">
                        <CheckCircle className="w-4 h-4 text-[#39FF14] mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
          <p className="text-center text-neutral-500 mt-10">
            Your capital is supported by <strong className="text-white">structure, strategy, and accountability</strong> — not guesswork.
          </p>
        </div>
      </section>

      {/* What Participants Receive */}
      <section className="py-16 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 mb-4">What Your Entrepreneurs Receive</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: BarChart3, text: "Business Clarity Assessment with personalized growth roadmap" },
              { icon: Building2, text: "Full access to the Business Solutions Center platform" },
              { icon: Handshake, text: "Consultant-led strategic guidance using R.A.P.I.D. framework" },
              { icon: Sparkles, text: "AI-powered tools for SOPs, business plans, time management" },
              { icon: LineChart, text: "KPI tracking, financial clarity, and performance dashboards" },
              { icon: FileText, text: "Impact & Accountability Reports with 50+ tracked data points" },
              { icon: Users, text: "Peer community with networking, pods, and opportunity marketplace" },
              { icon: BookOpen, text: "Resource vault with templates, guides, and training library" },
              { icon: Briefcase, text: "Project-based consulting for complex business challenges" }
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3 p-4 bg-neutral-50 rounded-xl border border-neutral-100">
                <item.icon className="w-5 h-5 text-[#39FF14] flex-shrink-0 mt-0.5" />
                <span className="text-sm text-neutral-700">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="py-16 px-4 sm:px-6 bg-[#39FF14]/5 border-y border-[#39FF14]/10">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 mb-4">Measurable Impact</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {OUTCOMES.map((o, idx) => (
              <div key={idx} className="text-center">
                <p className="text-4xl font-black text-[#39FF14]">{o.stat}</p>
                <p className="text-sm text-neutral-600 mt-2">{o.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Investment Options */}
      <section className="py-16 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 mb-4">Partnership Investment</h2>
            <p className="text-neutral-600">Flexible engagement models designed for institutional partners.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="border-2 border-neutral-200 hover:shadow-lg transition-shadow">
              <CardHeader>
                <Badge variant="outline" className="w-fit mb-2">Standard</Badge>
                <CardTitle>90-Day Engagement</CardTitle>
                <CardDescription>Ideal for standard borrower or grantee support</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-4xl font-bold text-neutral-900 mb-4">$1,500 <span className="text-base font-normal text-neutral-500">per entrepreneur</span></div>
                <ul className="space-y-2 text-sm text-neutral-600">
                  {["Full Business Solutions Center access", "Consultant-led R.A.P.I.D. guidance", "Business Clarity Assessment", "Progress reporting at 30/60/90 days"].map((f, i) => (
                    <li key={i} className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#39FF14]" />{f}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>
            <Card className="border-2 border-[#39FF14] bg-[#39FF14]/5 hover:shadow-lg transition-shadow relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2"><Badge className="bg-[#39FF14] text-black">Recommended</Badge></div>
              <CardHeader>
                <Badge variant="outline" className="w-fit mb-2 border-[#39FF14]/50 text-[#39FF14]">Enhanced</Badge>
                <CardTitle>6-Month Engagement</CardTitle>
                <CardDescription>For higher-touch support and deeper impact</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-4xl font-bold text-neutral-900 mb-4">$2,500 <span className="text-base font-normal text-neutral-500">per entrepreneur</span></div>
                <ul className="space-y-2 text-sm text-neutral-600">
                  {["Everything in 90-day engagement", "Extended support and accountability", "Advanced AI tools and custom SOPs", "Priority consultant access", "Enhanced impact reporting for stakeholders"].map((f, i) => (
                    <li key={i} className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#39FF14]" />{f}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
          <p className="text-center text-neutral-500 text-sm mt-6">Volume pricing, cohort pilots, and custom program structures available.</p>
        </div>
      </section>

      {/* Co-Branded Sponsor Portal */}
      <section className="py-16 px-4 sm:px-6 bg-gradient-to-r from-neutral-900 to-neutral-800 text-white">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-10">
            <div className="flex-1">
              <Badge className="bg-[#39FF14]/20 text-[#39FF14] border-[#39FF14]/30 mb-4">Included With Every Partnership</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Your Own Co-Branded <span className="text-[#39FF14]">Sponsor Portal</span>
              </h2>
              <p className="text-neutral-300 mb-6">
                Every partner organization receives a custom-branded portal page where your entrepreneurs can register directly into the program — with your logo and messaging alongside My Biz Consulting.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  { icon: Building2, text: "Your organization name, logo, and custom welcome message" },
                  { icon: Globe, text: "Unique portal URL you can share: yourorg.mybizconsulting.net/partner/your-org" },
                  { icon: Users, text: "Entrepreneurs register through your portal and are automatically tracked to your cohort" },
                  { icon: BarChart3, text: "Read-only Sponsor Dashboard to monitor cohort progress in real time" },
                  { icon: FileText, text: "Engagement metrics per member: points, connections, pods, posts, AI tool usage" },
                  { icon: Award, text: "Downloadable Impact & Accountability Reports for your stakeholders" }
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <item.icon className="w-5 h-5 text-[#39FF14] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-neutral-300">{item.text}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-3">
                <Button 
                  className="bg-[#39FF14] hover:bg-[#32E010] text-black font-semibold"
                  onClick={() => window.open("https://calendar.app.google/i7u5uTwU3QBcMopM8", "_blank")}
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Schedule Partnership Conversation
                </Button>
                <Button 
                  variant="outline"
                  className="border-neutral-600 text-white hover:bg-white/10"
                  onClick={() => window.open(`${window.location.origin}/partner/test-cdfi-foundation`, "_blank")}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  See Example Portal
                </Button>
              </div>
            </div>
            <div className="lg:w-[380px] flex-shrink-0">
              <Card className="bg-neutral-800 border-neutral-700 shadow-2xl">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4 pb-4 border-b border-neutral-700">
                    <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                      <Landmark className="w-5 h-5 text-blue-400" />
                    </div>
                    <span className="text-neutral-500 text-sm">+</span>
                    <div className="flex items-center gap-2">
                      <img src="/mybiz-logo.png" alt="MBC" className="h-6 w-auto" />
                      <div>
                        <p className="text-xs font-semibold text-white">My Biz Consulting</p>
                        <p className="text-[8px] text-[#39FF14] uppercase tracking-widest">BSC</p>
                      </div>
                    </div>
                  </div>
                  <p className="text-lg font-bold text-white mb-1">Your Organization Name</p>
                  <p className="text-xs text-[#39FF14] uppercase tracking-wider mb-4">Partner Program</p>
                  <div className="space-y-2 mb-4">
                    {["Cohort Members: 25", "Active: 22 (88%)", "Avg Points: 47", "Connections Made: 156"].map((s, i) => (
                      <div key={i} className="flex items-center justify-between py-1.5 border-b border-neutral-700/50 last:border-0">
                        <span className="text-xs text-neutral-400">{s.split(":")[0]}</span>
                        <span className="text-xs font-medium text-white">{s.split(":")[1]}</span>
                      </div>
                    ))}
                  </div>
                  <div className="bg-neutral-900 rounded-lg p-3 text-center">
                    <p className="text-[10px] text-neutral-500 uppercase mb-1">Portal URL</p>
                    <p className="text-xs text-[#39FF14] font-mono">/partner/your-organization</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 bg-neutral-950 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Ready to Strengthen Your <span className="text-[#39FF14]">Borrower & Grantee Outcomes?</span>
          </h2>
          <p className="text-neutral-400 max-w-2xl mx-auto mb-10">
            Let's explore how My Biz Consulting can integrate with your lending, grant, or economic development programs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-[#39FF14] hover:bg-[#32E010] text-black font-semibold h-14 px-8 text-base"
              onClick={() => window.open("https://calendar.app.google/i7u5uTwU3QBcMopM8", "_blank")}
              data-testid="cta-schedule-btn"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Schedule Partnership Conversation
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-neutral-600 text-white hover:bg-white/10 h-14 px-8"
              onClick={scrollToForm}
            >
              <Mail className="w-5 h-5 mr-2" />
              Submit Inquiry Form
            </Button>
          </div>
        </div>
      </section>

      {/* Inquiry Form */}
      <section className="py-16 px-4 sm:px-6" id="partnership-form">
        <div className="max-w-2xl mx-auto">
          {showForm || true ? (
            <Card className="border-0 shadow-xl">
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-2xl">Partnership Inquiry</CardTitle>
                <CardDescription>Tell us about your organization and goals. We'll respond within 1-2 business days.</CardDescription>
              </CardHeader>
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Organization Name *</Label>
                      <Input required placeholder="Your organization" value={formData.organization_name} onChange={(e) => setFormData({...formData, organization_name: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Organization Type</Label>
                      <select className="w-full p-3 border border-neutral-300 rounded-lg text-sm" value={formData.organization_type} onChange={(e) => setFormData({...formData, organization_type: e.target.value})}>
                        <option value="">Select type...</option>
                        <option value="cdfi">CDFI</option>
                        <option value="sba_lender">SBA Lender</option>
                        <option value="bank">Bank / Credit Union</option>
                        <option value="grant_program">Grant Program</option>
                        <option value="econ_dev">Economic Development Organization</option>
                        <option value="foundation">Foundation</option>
                        <option value="government">Government Agency</option>
                        <option value="nonprofit">Nonprofit</option>
                        <option value="accelerator">Accelerator / Incubator</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Contact Name *</Label>
                      <Input required placeholder="Your name" value={formData.contact_name} onChange={(e) => setFormData({...formData, contact_name: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Email *</Label>
                      <Input type="email" required placeholder="email@organization.com" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Phone</Label>
                      <Input type="tel" placeholder="(555) 123-4567" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Estimated Participants</Label>
                      <select className="w-full p-3 border border-neutral-300 rounded-lg text-sm" value={formData.estimated_participants} onChange={(e) => setFormData({...formData, estimated_participants: e.target.value})}>
                        <option value="">Select range...</option>
                        <option value="1-5">1-5 entrepreneurs</option>
                        <option value="6-15">6-15 entrepreneurs</option>
                        <option value="16-30">16-30 entrepreneurs</option>
                        <option value="31-50">31-50 entrepreneurs</option>
                        <option value="50+">50+ entrepreneurs</option>
                      </select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Preferred Engagement</Label>
                    <div className="grid grid-cols-2 gap-4">
                      {[{val: "90_day", label: "90-Day", price: "$1,500/person"}, {val: "6_month", label: "6-Month", price: "$2,500/person"}].map(opt => (
                        <label key={opt.val} className={`p-4 border-2 rounded-lg cursor-pointer transition-colors text-center ${formData.engagement_type === opt.val ? 'border-[#39FF14] bg-[#39FF14]/5' : 'border-neutral-200 hover:border-neutral-300'}`}>
                          <input type="radio" name="engagement_type" value={opt.val} checked={formData.engagement_type === opt.val} onChange={(e) => setFormData({...formData, engagement_type: e.target.value})} className="sr-only" />
                          <p className="font-semibold text-neutral-900">{opt.label}</p>
                          <p className="text-sm text-neutral-500">{opt.price}</p>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Additional Notes</Label>
                    <Textarea placeholder="Tell us about your current program goals, borrower/grantee support needs, or questions..." value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} className="min-h-[100px]" />
                  </div>
                  <Button type="submit" size="lg" className="w-full bg-[#39FF14] hover:bg-[#32E010] text-black font-semibold" disabled={submitting} data-testid="submit-inquiry-btn">
                    {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : <><Mail className="w-4 h-4 mr-2" /> Submit Partnership Inquiry</>}
                  </Button>
                </form>
              </CardContent>
            </Card>
          ) : null}
          <div className="mt-8 text-center">
            <p className="text-neutral-600 mb-4">Prefer to speak directly?</p>
            <Button variant="outline" size="lg" onClick={() => window.open("https://calendar.app.google/i7u5uTwU3QBcMopM8", "_blank")}>
              <Calendar className="w-4 h-4 mr-2" /> Schedule Partnership Conversation
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-neutral-950 text-white py-12">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <img src="/mybiz-logo.png" alt="My Biz Consulting" className="h-8 w-auto brightness-0 invert" />
            <div>
              <p className="text-sm font-semibold">My Biz Consulting</p>
              <p className="text-[10px] text-[#39FF14] uppercase tracking-widest">Business Solutions Center</p>
            </div>
          </div>
          <p className="text-neutral-400 text-sm mb-4">
            Contact: <a href="mailto:partnerships@mybizconsulting.net" className="text-white hover:text-[#39FF14] transition-colors">partnerships@mybizconsulting.net</a>
          </p>
          <div className="flex items-center justify-center gap-6 text-sm text-neutral-500 mb-4">
            <Link to="/" className="hover:text-neutral-300">Home</Link>
            <Link to="/pricing" className="hover:text-neutral-300">Services</Link>
            <Link to="/clarity-assessment" className="hover:text-neutral-300">Free Assessment</Link>
            <Link to="/security" className="hover:text-neutral-300">Security</Link>
            <a href="https://g.page/r/Cea96_JGj2rSEBM/review" target="_blank" rel="noopener noreferrer" className="hover:text-amber-400 flex items-center gap-1"><Star className="w-3 h-3" />Reviews</a>
          </div>
          <p className="text-neutral-600 text-xs">
            &copy; {new Date().getFullYear()} My Biz Consulting. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PartnershipInquiry;
